import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  booleanAttribute,
  inject,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ControlValueAccessor, NgControl, Validators } from '@angular/forms';
import { CdkListbox, CdkOption, ListboxValueChangeEvent } from '@angular/cdk/listbox';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { LUCIDE_ICONS } from '@compsych-ui-components/shared/assets/lucide-icons.generated';
import { DropdownLabelPosition, DropdownOption, DropdownSize } from './dropdown-types';

let nextId = 0;

/**
 * A labelled single-select dropdown built on the CDK Listbox (`@angular/cdk/listbox`)
 * and CDK Overlay. The trigger renders the closed field (label, placeholder/selected
 * value and chevron); the options open in an overlay panel with full keyboard support
 * (arrow keys, Home/End, typeahead) provided by the CDK listbox.
 *
 * Implements `ControlValueAccessor` so it works with both reactive forms
 * (`formControlName`, `[formControl]`) and template-driven forms (`ngModel`). The error
 * visual state is driven automatically by the bound `FormControl`'s validity once the
 * field has been touched, and can also be set explicitly via the `error` input.
 */
@Component({
  selector: 'compsych-dropdown',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CdkListbox, CdkOption, CdkConnectedOverlay, CdkOverlayOrigin],
  templateUrl: './dropdown.html',
  styleUrl: './dropdown.css',
})
export class DropdownComponent implements ControlValueAccessor, OnChanges {
  private readonly _autoId = `cmp-dropdown-${++nextId}`;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sanitizer = inject(DomSanitizer);
  private _onChange: (value: string) => void = () => {};
  private _onTouched: () => void = () => {};

  /** Injected when the component is used inside a reactive or template-driven form. */
  protected readonly ngControl: NgControl | null;

  constructor() {
    const ctrl = inject(NgControl, { optional: true, self: true });
    if (ctrl) ctrl.valueAccessor = this;
    this.ngControl = ctrl;
    this.chevronSvg = this.buildIconSvg('chevron-down');
  }

  @ViewChild('triggerBtn') private triggerBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('listboxEl') private listboxEl?: ElementRef<HTMLUListElement>;

  /** Label displayed above the trigger. */
  @Input() label = '';
  /** Text shown in the trigger when no option is selected. */
  @Input() placeholder = '';
  /** Currently selected value. When used in a form, the form controls the value. */
  @Input() value = '';
  /** Selectable options rendered in the overlay panel. */
  @Input() options: DropdownOption[] = [];
  /** Size variant — controls height, padding, and font size. Defaults to `'md'`. */
  @Input() size: DropdownSize = 'md';
  /** Native `id` attribute for the trigger. Auto-generated if omitted. */
  @Input() id?: string;
  /**
   * Id(s) of external elements that describe the field, space-separated.
   * Forwarded to `aria-describedby` on the trigger.
   */
  @Input() ariaDescribedBy?: string;
  /** Text displayed below the trigger. Shown in red when in error state. */
  @Input() supportingText = '';
  /**
   * Static error text shown in red when in error state. Used when the dropdown is
   * outside a form (with `[error]="true"`). When inside a form, prefer `errorMessages`.
   */
  @Input() errorMessage = '';
  /**
   * Map of Angular validator error keys to display strings. The component reads
   * `ngControl.errors` to determine which validator failed and shows the matching
   * message in red below the trigger. Example: `{ required: 'Please choose an option' }`
   */
  @Input() errorMessages: Record<string, string> = {};
  /**
   * Adds a blue asterisk after the label. When used in a form, this is set
   * automatically if the bound `FormControl` has `Validators.required`.
   * Supports boolean coercion.
   */
  @Input({ transform: booleanAttribute }) required = false;
  /**
   * Applies the error visual state (red border) explicitly. When used in a form,
   * the error state is driven automatically from the `FormControl`'s validity once
   * the field is touched — this input is only needed outside of forms. Supports boolean coercion.
   */
  @Input({ transform: booleanAttribute }) error = false;
  /** Disables the dropdown. When used in a form, also responds to `FormControl.disable()`. Supports boolean coercion. */
  @Input({ transform: booleanAttribute }) disabled = false;
  /** Visually hides the label while keeping it accessible to screen readers. Supports boolean coercion. */
  @Input({ transform: booleanAttribute }) hideLabel = false;
  /** Places the label above the trigger (`'top'`, default) or beside it (`'left'`). */
  @Input() labelPosition: DropdownLabelPosition = 'top';
  /**
   * Renders the label as the trigger's own resting text instead of a separate
   * label above/beside it — e.g. the trigger reads "I want to" until an option
   * is chosen. No visible external label is shown, but the `label` is still
   * exposed to assistive tech (a screen-reader-only `<label>` + `aria-labelledby`),
   * so the field is announced as a properly labelled combobox. Supports boolean coercion.
   */
  @Input({ transform: booleanAttribute }) inlineLabel = false;
  /** Lucide icon name rendered inside the trigger, before the displayed value/placeholder (e.g. `'globe'`). */
  @Input() leadingIcon?: string;
  /** Default Lucide icon shown before every option's label; each option's own `icon` (from the Icon Mapping) overrides it. */
  @Input() optionsLeadingIcon?: string;

  /** Emits the newly selected value whenever the user picks an option. */
  @Output() readonly valueChange = new EventEmitter<string>();
  /** Emitted when the overlay panel opens. */
  @Output() readonly opened = new EventEmitter<void>();
  /** Emitted when the overlay panel closes. */
  @Output() readonly closed = new EventEmitter<void>();

  protected readonly chevronSvg: SafeHtml;
  protected leadingIconSvg: SafeHtml | null = null;
  private readonly optionIconCache = new Map<string, SafeHtml>();

  protected isOpen = false;
  protected overlayWidth = 0;
  protected readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  ngOnChanges(): void {
    this.leadingIconSvg = this.leadingIcon ? this.buildIconSvg(this.leadingIcon) : null;
  }

  private buildIconSvg(name: string): SafeHtml {
    const svg = (LUCIDE_ICONS[name] ?? '').replace('<svg ', '<svg width="20" height="20" ');
    return this.sanitizer.bypassSecurityTrustHtml(svg);
  }

  /**
   * Scales trusted raw SVG (e.g. a flag) to 20px wide, preserving aspect ratio via its
   * viewBox. Only the root `<svg>` tag's width/height are normalized — inner elements
   * (rects/paths that draw the icon) keep their own dimensions.
   */
  private buildRawIconSvg(svg: string): SafeHtml {
    const sized = svg.replace(/<svg\b[^>]*>/, (openTag) =>
      openTag.replace(/\s(?:width|height)="[^"]*"/g, '').replace('<svg', '<svg width="20"')
    );
    return this.sanitizer.bypassSecurityTrustHtml(sized);
  }

  /**
   * Resolves an icon to cached SafeHtml: a trusted raw `iconSvg` wins, else a Lucide
   * `iconName`. Returns null when neither is set. Cached so rows keep a stable reference.
   */
  private resolveIcon(iconSvg: string | undefined, iconName: string | undefined): SafeHtml | null {
    if (iconSvg) {
      const key = 'raw:' + iconSvg;
      let svg = this.optionIconCache.get(key);
      if (!svg) {
        svg = this.buildRawIconSvg(iconSvg);
        this.optionIconCache.set(key, svg);
      }
      return svg;
    }
    if (iconName) {
      let svg = this.optionIconCache.get(iconName);
      if (!svg) {
        svg = this.buildIconSvg(iconName);
        this.optionIconCache.set(iconName, svg);
      }
      return svg;
    }
    return null;
  }

  /** Icon for an option row: its own `iconSvg`/`icon`, else the shared `optionsLeadingIcon`. */
  protected optionIcon(option: DropdownOption): SafeHtml | null {
    return this.resolveIcon(option.iconSvg, option.icon ?? this.optionsLeadingIcon);
  }

  /** Icon in the trigger: the selected option's own icon (flag/glyph), else the fixed `leadingIcon`. */
  get triggerIconSvg(): SafeHtml | null {
    return this.resolveIcon(this.selectedOption?.iconSvg, this.selectedOption?.icon) ?? this.leadingIconSvg;
  }

  get triggerId(): string {
    return this.id ?? this._autoId;
  }

  get labelId(): string {
    return this.triggerId + '-label';
  }

  get listboxId(): string {
    return this.triggerId + '-listbox';
  }

  get selectedOption(): DropdownOption | undefined {
    return this.options.find((o) => o.value === this.value);
  }

  /**
   * Resting text shown in the trigger: the selected option's label, else the
   * `label` in inline-label mode (so the label reads as the field's own text),
   * else the placeholder.
   */
  get triggerText(): string {
    return this.selectedOption?.label || (this.inlineLabel ? this.label : this.placeholder);
  }

  /** Value array bound to the CDK listbox (single-select, so 0 or 1 entry). */
  get listboxValue(): readonly string[] {
    if (!this.value || !this.options.some((o) => o.value === this.value)) return [];
    return [this.value];
  }

  /** True when the bound FormControl is invalid and touched, or when `error` is explicitly set. */
  get hasError(): boolean {
    return this.error || !!(this.ngControl?.invalid && this.ngControl.touched);
  }

  /** True when `required` is set explicitly, or when the bound FormControl has Validators.required. */
  get isRequired(): boolean {
    return this.required || !!this.ngControl?.control?.hasValidator(Validators.required);
  }

  private get activeErrorMessage(): string {
    const errors = this.ngControl?.errors;
    if (!errors) return '';
    for (const key of Object.keys(errors)) {
      if (this.errorMessages[key]) return this.errorMessages[key];
    }
    return '';
  }

  get displayedSupportingText(): string {
    if (this.hasError) {
      return this.activeErrorMessage || this.errorMessage || this.supportingText;
    }
    return this.supportingText;
  }

  get describedBy(): string | null {
    const parts: string[] = [];
    if (this.ariaDescribedBy) parts.push(this.ariaDescribedBy);
    if (this.supportingText || this.errorMessage || Object.keys(this.errorMessages).length) {
      parts.push(this.triggerId + '-supporting');
    }
    return parts.length ? parts.join(' ') : null;
  }

  get wrapperClasses(): string {
    return ['cmp-dropdown', this.size, this.disabled ? 'disabled' : '', this.labelPosition === 'left' ? 'label-left' : '']
      .filter(Boolean)
      .join(' ');
  }

  // ── Open / close ──────────────────────────────────────────────────────────

  protected toggle(): void {
    if (this.isOpen) {
      this.close(true);
    } else {
      this.open();
    }
  }

  protected open(): void {
    if (this.disabled || this.isOpen) return;
    this.overlayWidth = this.triggerBtn?.nativeElement.offsetWidth ?? 0;
    this.isOpen = true;
    this.opened.emit();
    this.cdr.markForCheck();
  }

  protected close(refocus = false): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this._onTouched();
    this.closed.emit();
    if (refocus) {
      setTimeout(() => this.triggerBtn?.nativeElement.focus());
    }
    this.cdr.markForCheck();
  }

  protected onPanelAttach(): void {
    setTimeout(() => this.listboxEl?.nativeElement.focus());
  }

  // ── Keyboard ──────────────────────────────────────────────────────────────

  protected onTriggerKeydown(event: KeyboardEvent): void {
    if (this.disabled) return;
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !this.isOpen) {
      event.preventDefault();
      this.open();
    } else if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      this.close(true);
    }
  }

  protected onListboxKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close(true);
    } else if (event.key === 'Tab') {
      event.preventDefault();
      this.close(true);
    }
  }

  protected onListboxValueChange(event: ListboxValueChangeEvent<string>): void {
    const next = event.value[0] ?? '';
    this.value = next;
    this._onChange(next);
    this.valueChange.emit(next);
    this.close(true);
  }

  // ── ControlValueAccessor ────────────────────────────────────────────────────

  writeValue(value: string | null): void {
    this.value = value ?? '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  /** Called by Angular when the bound FormControl is programmatically disabled or enabled. */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

}
